import { useCallback, useEffect, useMemo, useState } from 'react';
import { getExpertClientDailyNorms, getExpertClientNorms, updateExpertClientNorms } from '@/services/settings.service';
import {
  DAILY_NORM_KEYS,
  DailyNormsData,
  ExpertDailyNormsData,
  UpdateDailyNormsRequest,
} from '@/types/settings.types';

export interface ClientNorms {
  /** Стандартні норми клієнта (розрахунок / ручна правка клієнта). */
  standard: DailyNormsData | null;
  /** Власні норми поточного експерта. */
  expert: ExpertDailyNormsData | null;
  /** Ефективна норма для експерта по полях: власна ?? стандартна. */
  effective: DailyNormsData | null;
  loading: boolean;
  error: boolean;
  saveOwn: (norms: UpdateDailyNormsRequest) => Promise<void>;
}

/** Стандартні й власні норми клієнта для експерта; ефективна норма - для дашборда й шапки календаря. */
export function useClientNorms(clientId: string | number): ClientNorms {
  const [standard, setStandard] = useState<DailyNormsData | null>(null);
  const [expert, setExpert] = useState<ExpertDailyNormsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    Promise.all([
      getExpertClientDailyNorms(String(clientId)).then((response) => response?.data ?? null),
      // Власні норми - доповнення: збій не ховає стандартні.
      getExpertClientNorms(clientId).catch(() => null),
    ])
      .then(([standardNorms, expertNorms]) => {
        if (cancelled) return;
        setStandard(standardNorms);
        setExpert(expertNorms);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const saveOwn = useCallback(async (norms: UpdateDailyNormsRequest) => {
    setExpert(await updateExpertClientNorms(clientId, norms));
  }, [clientId]);

  const effective = useMemo(
    () =>
      standard
        ? ({ ...standard, ...Object.fromEntries(DAILY_NORM_KEYS.map((key) => [key, expert?.[key] ?? standard[key]])) } as DailyNormsData)
        : null,
    [standard, expert],
  );

  return { standard, expert, effective, loading, error, saveOwn };
}
